/**
 * React Query hooks for Achievements
 */

import { useQuery } from '@tanstack/react-query'
import { systemAchievementsApi, collectionAchievementsApi, badgesApi } from '@api/endpoints/achievements'

// ===================
// Query Keys
// ===================

export const ACHIEVEMENT_QUERY_KEYS = {
  all: ['achievements'] as const,
  // System achievements
  system: () => [...ACHIEVEMENT_QUERY_KEYS.all, 'system'] as const,
  systemAll: () => [...ACHIEVEMENT_QUERY_KEYS.system(), 'all'] as const,
  systemDetail: (id: string) => [...ACHIEVEMENT_QUERY_KEYS.system(), 'detail', id] as const,
  systemProgress: () => [...ACHIEVEMENT_QUERY_KEYS.system(), 'progress'] as const,
  systemEarned: () => [...ACHIEVEMENT_QUERY_KEYS.system(), 'earned'] as const,
  systemSummary: () => [...ACHIEVEMENT_QUERY_KEYS.system(), 'summary'] as const,
  systemUser: (userId: string) => [...ACHIEVEMENT_QUERY_KEYS.system(), 'user', userId] as const,
  // Collection achievements
  collection: () => [...ACHIEVEMENT_QUERY_KEYS.all, 'collection'] as const,
  collectionProgress: () => [...ACHIEVEMENT_QUERY_KEYS.collection(), 'progress'] as const,
  collectionCompleted: () => [...ACHIEVEMENT_QUERY_KEYS.collection(), 'completed'] as const,
  collectionUser: (userId: string) => [...ACHIEVEMENT_QUERY_KEYS.collection(), 'user', userId] as const,
  // Badges
  badges: () => [...ACHIEVEMENT_QUERY_KEYS.all, 'badges'] as const,
  badgesUser: (userId: string) => [...ACHIEVEMENT_QUERY_KEYS.badges(), 'user', userId] as const,
}

// ===================
// System Achievement Hooks
// ===================

/**
 * Get all visible system achievements with user's current progress
 */
export function useSystemAchievements() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.systemAll(),
    queryFn: () => systemAchievementsApi.getAll(),
  })
}

/**
 * Get single system achievement details
 */
export function useSystemAchievement(id: string) {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.systemDetail(id),
    queryFn: () => systemAchievementsApi.get(id),
    enabled: !!id,
  })
}

/**
 * Get user's progress on all system achievements
 */
export function useSystemAchievementsProgress() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.systemProgress(),
    queryFn: () => systemAchievementsApi.getMyProgress(),
  })
}

/**
 * Get only earned system achievements
 */
export function useEarnedSystemAchievements() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.systemEarned(),
    queryFn: () => systemAchievementsApi.getEarned(),
  })
}

/**
 * Get summary with totals and earned list
 */
export function useSystemAchievementsSummary() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.systemSummary(),
    queryFn: () => systemAchievementsApi.getSummary(),
  })
}

/**
 * Get achievements for a specific user (respects privacy settings)
 */
export function useUserSystemAchievements(userId: string | undefined) {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.systemUser(userId || ''),
    queryFn: () => systemAchievementsApi.getUserAchievements(userId!),
    enabled: !!userId,
  })
}

// ===================
// Collection Achievement Hooks
// ===================

/**
 * Get user's progress summary for all collection achievements
 */
export function useCollectionAchievementsProgress() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.collectionProgress(),
    queryFn: () => collectionAchievementsApi.getMyProgressSummary(),
  })
}

/**
 * Get completed collection achievement IDs
 */
export function useCompletedCollectionAchievements() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.collectionCompleted(),
    queryFn: () => collectionAchievementsApi.getCompleted(),
  })
}

/**
 * Get collection achievements for a specific user (respects privacy settings)
 */
export function useUserCollectionAchievements(userId: string | undefined) {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.collectionUser(userId || ''),
    queryFn: () => collectionAchievementsApi.getUserCollectionProgress(userId!),
    enabled: !!userId,
  })
}

// ===================
// Badge Hooks
// ===================

/**
 * Get current user's earned badges
 */
export function useMyBadges() {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.badges(),
    queryFn: () => badgesApi.getMyBadges(),
  })
}

/**
 * Get earned badges for a specific user (respects privacy settings)
 */
export function useUserBadges(userId: string | undefined) {
  return useQuery({
    queryKey: ACHIEVEMENT_QUERY_KEYS.badgesUser(userId || ''),
    queryFn: () => badgesApi.getUserBadges(userId!),
    enabled: !!userId,
  })
}
